import {Paragraph} from "@/pages/common/Home/Dissection/Article";

import avatar from "@images/home/analytics/evolving_role/avatar.png";
import challenges from "@images/home/analytics/evolving_role/challenges.png";
import future from "@images/home/analytics/evolving_role/future.png";
import identifying from "@images/home/analytics/evolving_role/identifying.png";
import with_ai from "@images/home/analytics/evolving_role/with_ai.png";
import without_ai from "@images/home/analytics/evolving_role/without_ai.png";
import {RootDesc} from "@ui/descriptions/RootDesc";
import {SmallDesc} from "@ui/descriptions/SmallDesc";
import {H2} from "@ui/titles/H2";
import {H3} from "@ui/titles/H3";
import {useSelector} from "react-redux";

import styles from "../styles.module.scss";

export const EvolvingRole = () => {
  const {isMobile} = useSelector((state) => state.common.settings);

  return (
    <>
      <Paragraph>
        <H2>
          <span>
            The Evolving Role of&nbsp;AI&nbsp;in&nbsp;Drug Submission
            and&nbsp;Approval: An&nbsp;Expert Perspective
          </span>
        </H2>

        <div className={styles.article_subtitle}>
          <i className={styles.article_label}>
            <i>
              <img
                src={avatar}
                alt={"avatar"}
              />
            </i>

            <div>
              <H3>
                <span>Marina Hickson</span>
              </H3>

              <RootDesc>
                <span>
                  Managing Director,{" "}
                  <a
                    href={"https://vivanti.eu/"}
                    target={"_blank"}
                    rel={"noopener noreferrer"}
                  >
                    Vivanti
                  </a>
                </span>
              </RootDesc>
            </div>
          </i>

          <RootDesc>
            <span>
              Artificial intelligence (AI) is&nbsp;transforming the
              pharmaceutical industry, revolutionising drug discovery and
              approval processes and reshaping how researchers identify
              potential drug candidates, streamline clinical trials, and ensure
              regulatory compliance.
            </span>

            <span>
              Integrating AI&nbsp;into these processes enhances efficiency,
              reduces costs, and accelerates new medications reaching the
              market. As&nbsp;pharmaceutical companies adopt
              AI&nbsp;technologies, it&nbsp;is&nbsp;essential to&nbsp;explore
              the opportunities and challenges they present in&nbsp;the quest
              for innovative healthcare solutions.
            </span>
          </RootDesc>
        </div>
      </Paragraph>

      <Paragraph>
        <H2>
          <span>
            Identifying Key Bottlenecks in&nbsp;the Drug Approval&nbsp;Process
          </span>
        </H2>

        <div className={styles.article_subtitle}>
          <RootDesc>
            <span>
              Traditional drug approval processes can be&nbsp;complex and
              time-consuming, with companies encountering obstacles
              at&nbsp;every stage, from clinical trials to&nbsp;regulatory
              reviews.
            </span>

            <span>
              One area burdened by&nbsp;inefficiencies is&nbsp;documentation.
              Clinical Study Reports (CSRs), essential for regulatory
              submissions, require meticulous data integration and adherence
              to&nbsp;compliance standards&nbsp;&mdash; labour intensive tasks
              that are prone to&nbsp;human error.
            </span>

            <span>
              In&nbsp;addition, managing documentation revisions and updating
              team members can be&nbsp;overwhelming. As&nbsp;regulations change,
              maintaining real-time compliance becomes a&nbsp;hurdle, frequently
              resulting in&nbsp;costly delays. The preparation of&nbsp;the
              Common Technical Document (CTD), a&nbsp;mandatory component
              involving extensive data compilation for regulatory submissions,
              exemplifies a&nbsp;process that could benefit from
              AI&nbsp;enhancements.
            </span>
          </RootDesc>

          <article style={{marginTop: "26rem"}}>
            <img
              src={identifying}
              alt={"image"}
            />
          </article>
        </div>
      </Paragraph>

      <Paragraph>
        <H2>
          <span>How AI&nbsp;Can Address These Challenges</span>
        </H2>

        <div className={styles.article_subtitle}>
          <RootDesc>
            <span>
              The integration of&nbsp;AI&nbsp;into drug submission and approval
              processes is&nbsp;recognised as&nbsp;a&nbsp;pivotal force within
              the pharmaceutical industry<sup>1</sup>. This evolution brings
              substantial benefits and notable challenges.
            </span>
          </RootDesc>
        </div>
      </Paragraph>

      <Paragraph>
        <H2>
          <span>
            Benefits of&nbsp;AI&nbsp;in&nbsp;Drug Submission and&nbsp;Approval:
          </span>
        </H2>

        <div className={styles.article_text_image}>
          <div
            style={{
              paddingLeft: isMobile ? "0" : "120rem",
              width: isMobile ? "100%" : "580rem",
            }}
          >
            <RootDesc>
              <H3>
                <span>Accelerated Drug Discovery and&nbsp;Development</span>
              </H3>

              <span>
                Traditional drug development timelines can take
                12&nbsp;to&nbsp;15&nbsp;years, with costs averaging around
                &#163;2.5&nbsp;billion per drug
                <sup>2</sup>. However, companies leveraging AI&nbsp;have reduced
                development time by&nbsp;25% to&nbsp;50%<sup>3</sup>.
              </span>

              <span>
                For instance, Insilico Medicine advanced
                an&nbsp;AI&nbsp;designed drug candidate through discovery and
                preclinical stages in&nbsp;just 30&nbsp;months<sup>4</sup>.
              </span>
            </RootDesc>
          </div>

          <div className={styles.article_text_cards_benefits}>
            <div
              style={{backgroundImage: `url(${without_ai})`}}
              className={styles.article_card_ai}
            >
              <i style={{background: "var(--blueLight)"}}>
                <SmallDesc>
                  <span>Without AI</span>
                </SmallDesc>
              </i>

              <div>
                <strong>12–15</strong>
                <br />
                <span>years</span>
              </div>

              <div>
                <strong>~ &#163;2.5</strong>
                <br />
                <span>
                  billion per drug<sup>2</sup>
                </span>
              </div>
            </div>

            <div
              style={{backgroundImage: `url(${with_ai})`}}
              className={styles.article_card_ai}
            >
              <i
                style={{
                  background:
                    "linear-gradient(135deg, #8692CB 0%, #B262C9 100%)",
                }}
              >
                <SmallDesc>
                  <span>With AI</span>
                </SmallDesc>
              </i>

              <H3>
                <span>AI&nbsp;have reduced development time&nbsp;by</span>
              </H3>

              <div>
                <strong>25–50</strong>
                <span>
                  %<sup>3</sup>
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.article_subtitle}>
          <H3>
            <span>Improved Accuracy and Efficiency</span>
          </H3>

          <RootDesc>
            <span>
              By&nbsp;automating data collection from various sources,
              AI&nbsp;can minimise human error&nbsp;&mdash; a&nbsp;crucial
              factor given approximately 90% of&nbsp;drugs tested
              in&nbsp;clinical trials fail to&nbsp;gain approval<sup>5</sup>.
              Furthermore, AI&nbsp;applications can optimise lead compound
              identification and predict pharmacokinetics and toxicity,
              facilitating more informed decision-making throughout development.
            </span>
          </RootDesc>
        </div>

        <div className={styles.article_subtitle}>
          <H3>
            <span>Real-time Compliance Monitoring</span>
          </H3>

          <RootDesc>
            <span>
              AI&nbsp;systems can continuously monitor regulatory changes,
              ensuring documentation meets the latest standards. This capability
              is&nbsp;vital as&nbsp;regulatory landscapes evolve rapidly;
              significantly reducing the risk of&nbsp;compliance breaches
              <sup>6</sup>.
            </span>
          </RootDesc>
        </div>

        <div className={styles.article_subtitle}>
          <H3>
            <span>Template-driven Document Assembly</span>
          </H3>

          <RootDesc>
            <span>
              Using historical data and predefined templates, AI&nbsp;can
              automate the creation of&nbsp;detailed CSRs and CTDs,
              significantly reducing the manual effort involved.
            </span>
          </RootDesc>
        </div>

        <div className={styles.article_subtitle}>
          <H3>
            <span>Streamlined Documentation Revisions</span>
          </H3>

          <RootDesc>
            <span>
              AI&nbsp;can manage changes to&nbsp;documents while alerting team
              members to&nbsp;updates in&nbsp;real time. This ensures alignment
              and minimises the risk of&nbsp;inconsistencies in&nbsp;the
              document management process.
            </span>
          </RootDesc>
        </div>
      </Paragraph>

      <Paragraph>
        <H2>
          <span>The Challenges of&nbsp;Implementing AI</span>
        </H2>

        <div className={styles.article_subtitle}>
          <article>
            <img
              src={challenges}
              alt={"image"}
            />
          </article>

          <RootDesc>
            <span>
              Despite these advantages, integrating AI&nbsp;into established
              processes presents several challenges:
            </span>

            <H3>
              <span>Data Quality and Integration Issues</span>
            </H3>

            <span>
              High-quality data is&nbsp;essential for effective
              AI&nbsp;algorithms. However, obtaining comprehensive datasets from
              clinical trials can be&nbsp;complex due to&nbsp;standardisation
              issues, incomplete data, and variability/volume. Additionally,
              integrating data from various platforms while ensuring privacy and
              security remains a&nbsp;significant challenge<sup>7</sup>.
            </span>

            <H3>
              <span>Transparency and Interpretability</span>
            </H3>

            <span>
              The &laquo;black box&raquo; nature of&nbsp;many AI&nbsp;models
              complicates transparency in&nbsp;decision-making processes
              <sup>8</sup>. Regulatory bodies require clear explanations for
              AI&nbsp;generated outputs, which can be&nbsp;difficult when models
              operate without interpretable logic. This lack
              of&nbsp;transparency can undermine trust among stakeholders.
            </span>

            <H3>
              <span>Regulatory Frameworks</span>
            </H3>

            <span>
              The absence of&nbsp;standardised protocols for validating
              AI&nbsp;driven processes complicates technology adoption.
              Regulatory agencies are still developing guidelines for overseeing
              AI&nbsp;applications in&nbsp;drug development, leading
              to&nbsp;uncertainty that may hinder innovation<sup>9</sup> (Penn
              State Library Guides, 2023).
            </span>

            <span>
              Addressing the challenges of&nbsp;integrating AI&nbsp;into
              clinical trials is&nbsp;crucial for advancing drug development
              processes. However, overcoming these hurdles requires not only
              technological solutions but also a&nbsp;focus on&nbsp;upskilling
              the workforce to&nbsp;efficiently leverage AI&nbsp;capabilities.
            </span>
          </RootDesc>
        </div>
      </Paragraph>

      <Paragraph>
        <H2>
          <span>
            Upskilling Recommendations for the Evolving Role
            of&nbsp;AI&nbsp;in&nbsp;Drug Submission and Approval
          </span>
        </H2>

        <div className={styles.article_subtitle}>
          <RootDesc>
            <span>
              The complexity of&nbsp;obtaining and integrating high-quality
              data, ensuring transparency in&nbsp;AI&rsquo;s decision-making
              processes, and navigating evolving regulatory frameworks demands
              a&nbsp;well-prepared team. Upskilling initiatives such
              as&nbsp;role-based simulations and the use of&nbsp;AI-driven tools
              can equip staff with the necessary skills and knowledge.
            </span>

            <i className={styles.article_label}>
              <i>
                <span>1</span>
              </i>

              <div>
                <H3>
                  <span style={{fontSize: "28rem"}}>
                    Implement Role-Based Simulations
                  </span>
                </H3>
              </div>
            </i>

            <H3>
              <span>&laquo;A&nbsp;Day in&nbsp;the Life&raquo; Simulations</span>
            </H3>

            <span>
              Create immersive experiences like &laquo;A&nbsp;Day in&nbsp;the
              Life of&nbsp;a&nbsp;Biologist&raquo; or&nbsp;&laquo;A&nbsp;Day
              in&nbsp;the Life of&nbsp;a&nbsp;Clinical Researcher.&quot; Using
              virtual or&nbsp;augmented reality allows employees
              to&nbsp;experience the daily roles and challenges
              of&nbsp;colleagues, like biologists or&nbsp;clinical researchers.
              To&nbsp;implement this, a&nbsp;company would define crucial roles,
              craft accurate scenarios reflecting these roles, and employ
              immersive technology for a&nbsp;realistic experience. This method
              promotes understanding and teamwork across departments, enhancing
              organisational efficiency.
            </span>

            <H3>
              <span>Scenario-Based Training</span>
            </H3>

            <span>
              Develop AI-driven simulations that allow teams to&nbsp;engage
              in&nbsp;real-time scenarios related to&nbsp;drug development and
              approval. This practical training prepares employees
              to&nbsp;navigate complex situations and appreciate the impact
              of&nbsp;their decisions on&nbsp;others.
            </span>

            <i className={styles.article_label}>
              <i>
                <span>2</span>
              </i>

              <div>
                <H3>
                  <span style={{fontSize: "28rem"}}>
                    Use AI&nbsp;Chatbots for Knowledge Exchange
                  </span>
                </H3>
              </div>
            </i>

            <H3>
              <span>Deploy Document Intelligence Chatbots</span>
            </H3>

            <span>
              Introduce AI&nbsp;chatbots focused on&nbsp;document intelligence
              to&nbsp;provide quick access to&nbsp;regulatory guidelines,
              project updates, and best practices. This reduces time spent
              searching for information, allowing teams to&nbsp;concentrate
              on&nbsp;their core tasks.
            </span>

            <span>
              To&nbsp;effectively implement AI&nbsp;chatbots for document
              intelligence, companies should pinpoint key information needs and
              select a&nbsp;compatible technology that integrates with existing
              systems. Chatbots need to&nbsp;be&nbsp;customised and trained with
              specific data before pilot testing and full deployment, which
              should include comprehensive staff training. Continuous
              maintenance and a&nbsp;system for feedback will ensure the chatbot
              remains effective and meets evolving organisational requirements.
              Most pharmaceutical companies opt to&nbsp;work in&nbsp;partnership
              with third party vendors to&nbsp;develop bespoke solutions
              <sup>10</sup>.
            </span>

            <H3>
              <span>Promote Continuous Learning</span>
            </H3>

            <span>
              Equip chatbots to&nbsp;answer questions about industry news,
              technology trends and cases from events. This encourages
              a&nbsp;culture of&nbsp;continuous learning, ensuring employees
              remain informed about industry developments.
            </span>
          </RootDesc>
        </div>
      </Paragraph>

      <Paragraph>
        <H2>
          <span>Benefits of&nbsp;upskilling your teams in&nbsp;AI</span>
        </H2>

        <div className={styles.article_subtitle}>
          <RootDesc>
            <span>
              There are significant benefits to&nbsp;be&nbsp;realised
              by&nbsp;companies who train and upskill their workforce
              to&nbsp;use AI&nbsp;tools effectively including:
            </span>

            <ul>
              <li>
                <b>Enhanced Collaboration:</b> Improved understanding
                of&nbsp;roles leads to&nbsp;more effective teamwork.
              </li>

              <li>
                <b>Increased Engagement:</b> Immersive training methods
                encourage active participation and deeper learning.
              </li>

              <li>
                <b>Greater Efficiency:</b> AI&nbsp;chatbots streamline
                information access, facilitating quicker decision-making.
              </li>
            </ul>

            <span>
              By&nbsp;implementing these training tips, pharmaceutical companies
              can develop a&nbsp;workforce adept at&nbsp;leveraging
              AI&nbsp;technologies while promoting better collaboration
              throughout drug submission and approval processes.
            </span>
          </RootDesc>
        </div>
      </Paragraph>

      <Paragraph>
        <H2>
          <span>Successful Case Studies</span>
        </H2>

        <div className={styles.article_subtitle}>
          <RootDesc>
            <span>
              Several companies have successfully integrated AI&nbsp;into their
              drug development processes:
            </span>

            <ul>
              <li>
                <b>Bristol Myers Squibb</b> used machine learning algorithms
                to&nbsp;streamline clinical trial designs, resulting
                in&nbsp;faster patient recruitment and reduced costs
                <sup>11</sup>
                (Statista, 2023).
              </li>

              <li>
                <b>AstraZeneca</b> implemented an&nbsp;AI-driven platform for
                real-time monitoring of&nbsp;clinical trial data, enhancing
                compliance tracking and decision-making efficiency.
              </li>

              <li>
                <b>Asklepios Biopharmaceutical (AskBio)</b> acquired
                by&nbsp;Bayer integrated AI&nbsp;into their gene therapy
                development processes to&nbsp;understand gene regulation better
                and identify new regulatory sequences within genomes
                <sup>12</sup>.
              </li>

              <li>
                <b>
                  Several biopharmaceutical companies, such as&nbsp;Bayer,
                  Roche, and Pfizer,
                </b>{" "}
                have teamed up&nbsp;with IT&nbsp;companies to&nbsp;develop
                a&nbsp;platform for the discovery of&nbsp;therapies
                in&nbsp;areas such as&nbsp;immuno-oncology and cardiovascular
                diseases<sup>13</sup>.
              </li>
            </ul>
          </RootDesc>
        </div>
      </Paragraph>

      <Paragraph>
        <H2>
          <span>Reflecting on&nbsp;the Future</span>
        </H2>

        <div className={styles.article_subtitle}>
          <RootDesc>
            <span>
              The integration of&nbsp;AI&nbsp;into drug submission and approval
              processes signifies a&nbsp;shift towards more efficient
              pharmaceutical development. The potential benefits are substantial
              including faster approvals and quicker access to&nbsp;essential
              medications while maintaining rigorous standards.
            </span>

            <span>
              Applying AI&nbsp;in&nbsp;drug submissions and approvals promises
              a&nbsp;new era of&nbsp;efficiency and precision.
              By&nbsp;proactively addressing obstacles and using innovative
              solutions, we&nbsp;can improve drug development for patients and
              the pharmaceutical industry.
            </span>

            <span>
              Reflecting on&nbsp;the upskilling tips outlined, implementing
              role-based simulations, deploying AI&nbsp;chatbots, and promoting
              continuous learning are essential for equipping your workforce
              to&nbsp;navigate the evolving landscape of&nbsp;drug submission
              and approval.
            </span>

            <span>
              By&nbsp;partnering with Vivanti, pharmaceutical companies can
              initiate this process easily. We&nbsp;develop engaging training
              through immersive content and design immersive simulations that
              enhance understanding across roles. Our expertise
              in&nbsp;developing and deploying customised AI&nbsp;chatbots
              ensures quick access to&nbsp;vital information. For insights
              on&nbsp;how Vivanti is&nbsp;contributing to&nbsp;this
              transformation through our services, please visit our{" "}
              <a
                style={{textDecoration: "underline"}}
                href={"https://vivanti.eu/"}
                target={"_blank"}
                rel={"noopener noreferrer"}
              >
                website
              </a>
              .
            </span>
          </RootDesc>

          <article style={{marginTop: "24rem"}}>
            <img
              src={future}
              alt={"image"}
            />
          </article>
        </div>
      </Paragraph>

      <Paragraph>
        <div className={styles.article_citations}>
          <h6>References</h6>

          <RootDesc>
            <ol>
              <li>
                <a
                  href={
                    "https://www.europeanpharmaceuticalreview.com/news/186633/cphi-ai-to-revolutionise-drug-development-by-2026/"
                  }
                  target={"_blank"}
                  rel={"noopener noreferrer"}
                >
                  https://www.europeanpharmaceuticalreview.com/news/186633/cphi-ai-to-revolutionise-drug-development-by-2026/.
                </a>
              </li>

              <li>
                <a
                  href={
                    "https://phrma.org/policy-issues/Research-and-Development-Policy-Framework"
                  }
                  target={"_blank"}
                  rel={"noopener noreferrer"}
                >
                  https://phrma.org/policy-issues/Research-and-Development-Policy-Framework.
                </a>
              </li>

              <li>
                <a
                  href={"https://www.n-ix.com/ai-trends/"}
                  target={"_blank"}
                  rel={"noopener noreferrer"}
                >
                  https://www.n-ix.com/ai-trends/.
                </a>
              </li>

              <li>
                <span>
                  Nature. (2023). Advancements in&nbsp;artificial intelligence
                  for drug discovery: A&nbsp;case study on&nbsp;Insilico
                  Medicine.
                </span>
              </li>

              <li>
                <span>
                  FDA. (2023). Artificial intelligence in&nbsp;drug development:
                  Current trends and future perspectives.
                </span>
              </li>

              <li>
                <a
                  href={
                    "https://www.certa.ai/blogs/the-future-of-ai-in-compliance-trends-to-watch"
                  }
                  target={"_blank"}
                  rel={"noopener noreferrer"}
                >
                  https://www.certa.ai/blogs/the-future-of-ai-in-compliance-trends-to-watch.
                </a>
              </li>

              <li>
                <span>
                  Scribbr. (2021). How to&nbsp;cite a&nbsp;journal article |
                  APA, MLA & Chicago examples.
                </span>
              </li>

              <li>
                <span>
                  Artificial_Intelligence_and_Transparency_Opening_the_Black_Box.
                </span>
              </li>

              <li>
                <span>Penn State Library Guides, (2023).</span>
              </li>

              <li>
                <a
                  href={
                    "https://www.mckinsey.com/industries/healthcare/our-insights/generative-ai-in-healthcare-adoption-trends-and-whats-next"
                  }
                  target={"_blank"}
                  rel={"noopener noreferrer"}
                >
                  https://www.mckinsey.com/industries/healthcare/our-insights/generative-ai-in-healthcare-adoption-trends-and-whats-next.
                </a>
              </li>

              <li>
                <span>
                  Statista. (2023). Machine learning applications
                  in&nbsp;pharmaceutical research: A&nbsp;statistical overview.
                </span>
              </li>

              <li>
                <a
                  href={"https://www.drugdiscoverytrends"}
                  target={"_blank"}
                  rel={"noopener noreferrer"}
                >
                  https://www.drugdiscoverytrends.
                </a>
              </li>

              <li>
                <a
                  href={"https://www.ncbi.nlm.nih.gov/pmc/articles/PMC7577280/"}
                  target={"_blank"}
                  rel={"noopener noreferrer"}
                >
                  https://www.ncbi.nlm.nih.gov/pmc/articles/PMC7577280/.
                </a>
              </li>
            </ol>
          </RootDesc>
        </div>
      </Paragraph>
    </>
  );
};
